import React, { useState, useEffect } from 'react';
import { usePermisosContext } from '../../context/PermisosContext';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Paper
} from '@mui/material';
import { Obra } from '../../types';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';

interface SubirDocumentoFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    file: File;
    nombre: string;
    obraId: string;
  }) => Promise<void>;
  obras: Obra[];
  loading?: boolean;
  defaultObraId?: string;
}

const SubirDocumentoForm: React.FC<SubirDocumentoFormProps> = ({
  open,
  onClose,
  onSubmit,
  obras,
  loading = false,
  defaultObraId = ''
}) => {
  const { verificarPermisosObra } = usePermisosContext();
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [nombre, setNombre] = useState('');
  const [obraId, setObraId] = useState(defaultObraId);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setObraId(defaultObraId);
  }, [defaultObraId]);

  useEffect(() => {
    if (obras.length === 1) {
      setObraId(obras[0].id);
    }
  }, [obras]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);

      // Establecer nombre por defecto del archivo
      if (!nombre) {
        setNombre(selectedFile.name);
      }

      // Si es una imagen, crear preview
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file || !nombre) {
      setError('Por favor complete todos los campos requeridos');
      return;
    }

    const selectedObraId = obras.length === 1 ? obras[0].id : obraId;

    // Solo verificar permisos si se seleccionó una obra
    if (selectedObraId && !verificarPermisosObra(selectedObraId)) {
      setError('No tienes permisos para subir documentos en esta obra');
      return;
    }

    try {
      await onSubmit({
        file,
        nombre,
        obraId: selectedObraId || '',
      });
      handleReset();
    } catch (err: any) {
      setError(err.message || 'Error al subir el documento');
    }
  };

  const handleReset = () => {
    setFile(null);
    setNombre('');
    setObraId('');
    setPreviewUrl(null);
    setError(null);
  };

  const renderPreview = () => {
    if (!file) return null;

    if (previewUrl) {
      return (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <Paper elevation={3} sx={{ p: 1, borderRadius: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 4
              }}
            />
          </Paper>
          <IconButton
            size="small"
            onClick={() => {
              setFile(null);
              setPreviewUrl(null);
            }}
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
          bgcolor: 'grey.50',
          borderRadius: 2,
          position: 'relative'
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="body2" color="text.primary" fontWeight="medium">
          {file.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </Typography>
        <IconButton
          size="small"
          onClick={() => setFile(null)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          Subir Nuevo Documento
        </Typography>
        {!loading && (
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <DialogContent dividers sx={{ flex: 1 }}>
          {error && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            {!file && (
              <>
                <input
                  accept=".pdf,.doc,.docx,.dwg,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  id="documento-upload"
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <label htmlFor="documento-upload" style={{ width: '100%', display: 'block' }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'primary.100',
                        borderColor: 'primary.dark'
                      }
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                      Seleccionar Archivo
                    </Typography>
                    <Typography variant="caption" color="text.secondary" align="center">
                      PDF, Word, DWG, Imágenes (Max 20MB)
                    </Typography>
                  </Paper>
                </label>
              </>
            )}
            {renderPreview()}
          </Box>

          <TextField
            label="Nombre del Documento"
            fullWidth
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            sx={{ mb: 3 }}
            disabled={loading}
            variant="outlined"
          />

          <FormControl fullWidth sx={{ mb: 2 }} disabled={loading}>
            <InputLabel>Obra</InputLabel>
            <Select
              value={obraId}
              onChange={(e) => setObraId(e.target.value)}
              label="Obra"
            >
              <MenuItem value="">
                <em>Sin obra</em>
              </MenuItem>
              {obras.map((obra) => (
                <MenuItem
                  key={obra.id}
                  value={obra.id}
                  disabled={!verificarPermisosObra(obra.id)}
                >
                  {obra.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress sx={{ borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                Subiendo documento...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!file || !nombre || loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              boxShadow: 2
            }}
          >
            Subir Documento
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubirDocumentoForm;